import React, { useState, useEffect } from "react";
import TotalNoCard from "../../components/TotalNoCard/TotalNoCard";
import { BeaconStyle } from "./Beacon.styled";
import { HeadingStyle } from "../../styles/HeadingStyles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getBeaconDataAPI,
  getLatestBeaconRecords,
  getSnoozeStatus,
  getWarrantyData,
} from "../../api/BeaconAPI";
import DeleteButton from "../../components/Buttons/DeleteButton";
import { toast } from "react-toastify";
import moment from "moment";
import { useDB } from "../../context/DynamoDBContext";
import { useReload } from "../../context/ReloadContext";
import useInternetConnectivity from "../../hooks/useInternetConnectivity";
import Internet from "../../components/internet/Internet";
import DeleteStatus from "../../components/internet/DeleteStatus";
import { useSelector } from "react-redux";
import ExportData from "../../components/Export/ExportData";
import Popup from "../../components/Modals/Modal";
import api from "../../api/configAxios";
import Controller from "../../common/controller";
import { capitalizeFirstLetter } from "../../common/capitalizeFirstLetter";
import { getConfigurations } from "../../api/ConfigurationApi";
import { PageStyle } from "../../styles/MainBodyStyles";

const Beacon = ({ title, id, email }) => {
  const { dynamoDBClient } = useDB();
  const isOnline = useInternetConnectivity();
  const { reload, setReload } = useReload();
  const queryClient = useQueryClient();
  const navigateRed = useSelector((state) => state.navigateRed);
  const { access_token } = useSelector((state) => state.userKeyRed);
  const [userBMac, setUserBMac] = useState("xxxxxxxxx");
  const [isDelete, setIsDelete] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userBattery, setUserBattery] = useState("0%");
  const [userBLocation, setUserBLocation] = useState("Not Available");
  const [bStatus, setBStatus] = useState("Not Available");
  const [record, setRecord] = useState([]);
  const [snooze, setSnooze] = useState("off");
  const [enableBeaconAPI, setEnableBeaconAPI] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tableHistoryStatus, setTableHistoryStatus] = useState("Loading ... ");
  const [warranty, setWarranty] = useState("...");
  const [signal, setSignal] = useState(0);
  const [signalToUse, setSignalToUse] = useState(0);
  const [redSignalRange, setRedSignalRange] = useState(0);
  const [greenSignalRange, setGreenSignalRange] = useState(0);
  const rssiImages = [
    "0.png",
    "10.png",
    "20.png",
    "30.png",
    "40.png",
    "50.png",
    "60.png",
    "70.png",
    "80.png",
    "90.png",
    "100.png",
  ];

  useEffect(() => {
    setConfirmDelete(false);
    setIsDelete(false);
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (reload) {
      queryClient.fetchQuery(["BeaconDataAPI", id]);
      queryClient.fetchQuery(["BeaconSnooze", id]);
      queryClient.fetchQuery(["LatestBeaconRecord", id]);
      queryClient.fetchQuery(["getRssiConfigurations"]);
    }
  }, [reload]);

  useEffect(() => {
    if (dynamoDBClient !== null) {
      setEnableBeaconAPI(true);
    } else {
      setEnableBeaconAPI(false);
    }
  }, [dynamoDBClient]);

  const handleError = (type) => {
    if (type === "getRssiConfigurations") {
      toast.error("Unable to get RSSI Configurations", {
        bodyClassName: "notification-div-error",
        progressClassName: "progress-bar-error",
      });
    }
    if (type === "BeaconDataAPI") {
      toast.error("Unable to get Beacon Data", {
        bodyClassName: "notification-div-error",
        progressClassName: "progress-bar-error",
      });
    }
    if (type === "BeaconWarranty") {
      toast.error("Unable to get Beacon Warranty", {
        bodyClassName: "notification-div-error",
        progressClassName: "progress-bar-error",
      });
    }
    if (type === "BeaconSnooze") {
      toast.error("Unable to get Beacon Snooze Settings", {
        bodyClassName: "notification-div-error",
        progressClassName: "progress-bar-error",
      });
    }
    if (type === "LatestBeaconRecord") {
      toast.error("Error While Fetching Beacon Notifications", {
        bodyClassName: "notification-div-error",
        progressClassName: "progress-bar-error",
      });
    }
  };

  const setBeaconData = (data = []) => {
    if (data.length !== 0) {
      const info = data[0];
      // setUserEmail(info.Email)
      setUserBMac(info.BeaconMAC);
      setUserBattery(`${info.Battery} %`);
      setUserBLocation(info.BeaconLocation ? info.BeaconLocation : "N/A");
      const word = capitalizeFirstLetter(info.status);
      setBStatus(word);
      setSignal(info.signal);
      let imageIndex = Math.floor(info.signal / 10 + 1);
      setSignalToUse(
        () => rssiImages[Math.min(imageIndex, rssiImages.length - 1)]
      );
      // setUserFirmware(info.Firmware)
      // setUserQR(info.QRCode)
    }
  };

  const { data: configData, status: configStatus } = useQuery({
    queryKey: ["getRssiConfigurations"],
    queryFn: () => getConfigurations(dynamoDBClient, "RSSI"),
    enabled: enableBeaconAPI,
    onSuccess: (data) => {
      if (data.length > 0) {
        const config = data[0];

        let redRange = +config.ConfigurationData.Red;
        let greenRange = +config.ConfigurationData.Green;

        setRedSignalRange(redRange);
        setGreenSignalRange(greenRange);
      } else {
      }
    },
    onError: (error) => {
      console.log("getRssiConfigurations :", error);
      handleError("getRssiConfigurations");
    },
  });

  const { data: beaconData } = useQuery({
    queryKey: ["BeaconDataAPI", id],
    queryFn: () => getBeaconDataAPI(dynamoDBClient, id, email),
    enabled: enableBeaconAPI,
    onSuccess: (data) => {
      if (data.length > 0) {
        setBeaconData(data);
      } else {
        setConfirmDelete(true);
        setIsDelete(true);
      }
    },
    onError: (error) => {
      console.log("BeaconDataAPI :", error);
      setEnableBeaconAPI(false);
      handleError("BeaconDataAPI");
    },
  });

  const { data: warrantyData } = useQuery({
    queryKey: ["BeaconWarranty", id],
    queryFn: () => getWarrantyData(dynamoDBClient, id),
    enabled: enableBeaconAPI,
    onSuccess: (data) => {},
    onError: (error) => {
      console.log("BeaconWarranty :", error);
      handleError("BeaconWarranty");
    },
  });

  const setSnoozeData = (data) => {
    setSnooze(data);
  };

  const { snoozeData } = useQuery({
    queryKey: ["BeaconSnooze", id],
    queryFn: () => getSnoozeStatus(dynamoDBClient, id, email),
    enabled: enableBeaconAPI,
    onSuccess: (data) => {
      setSnoozeData(data);
    },
    onError: (error) => {
      console.log("BeaconSnooze :", error);
      handleError("BeaconSnooze");
    },
  });

  const calculateRecordData = (data = []) => {
    if (data.length !== 0) {
      setRecord(data);
    } else {
      setRecord([]);
      setTableHistoryStatus("No History Available");
    }
  };

  const { status: recordStatus, data: recordData } = useQuery({
    queryKey: ["LatestBeaconRecord", id],
    queryFn: () => getLatestBeaconRecords(dynamoDBClient, id, email),
    enabled: enableBeaconAPI,
    onSuccess: (data = []) => {
      calculateRecordData(data);
    },
    onError: (error) => {
      console.log("Error While Fetching Beacon Notifications: ", error);
      handleError("LatestBeaconRecord");
    },
  });

  useEffect(() => {
    if (beaconData) {
      setBeaconData(beaconData);
    }

    if (snoozeData) {
      setSnoozeData(snoozeData);
    }

    if (recordData) {
      calculateRecordData(recordData);
    }
  }, [beaconData, snoozeData, recordData]);

  const deleteBeacon = async () => {
    setIsDelete(true);
    try {
      var response = await api.post(
        `${Controller.dynamoDB}/deleteBeacon`,
        {
          email: email.toLocaleLowerCase(),
          mac: id,
        },
        {
          headers: {
            Authorization: "Bearer " + access_token,
          },
        }
      );
      setConfirmDelete(true);
      toast("Beacon Deleted Successfully.Refresh Tree");
      setIsDelete(true);
      setReload();
    } catch (error) {
      setIsDelete(false);
      toast.error("Unable to delete Beacon", {
        bodyClassName: "notification-div-error",
        progressClassName: "progress-bar-error",
      });
    }
  };

  return (
    <BeaconStyle>
      <PageStyle>
        <ExportData />
        <Popup
          title="Delete Beacon"
          visible={showModal}
          setVisible={() => setShowModal(false)}
          message={
            "This Action will DELETE the Beacon. Are you sure you want to proceed?"
          }
          onAccept={() => {
            setShowModal(false);
            deleteBeacon();

            // setIsDelete(true)
          }}
        />
        <div
          className={navigateRed.sideBar ? "home-container" : "home-container2"}
        >
          {!isOnline && <Internet />}
          {confirmDelete && <DeleteStatus />}
          <HeadingStyle>
            {title}&ensp;
            {bStatus == "Offline" || bStatus === "Not Available" ? (
              <div className="signal-div">
                <img
                  src={`${process.env.PUBLIC_URL}/BeaconImages/0.png`}
                  className="signal-img"
                  alt="beacon Signal"
                />
                <p className="signal-text">0</p>
              </div>
            ) : (
              <div className="signal-div">
                <img
                  src={`${process.env.PUBLIC_URL}/BeaconImages/${signalToUse}`}
                  className="signal-img"
                  alt="beacon Signal"
                />
                <p className="signal-text">{signal}</p>
              </div>
            )}
            {/* <i
            className={`bi bi-reception-4 ${
              bStatus == "Offline" ? (
                <img src={`${process.env.PUBLIC_URL}/BeaconImages/0.png`} />
              ) : signal <= greenSignalRange ? (
                "green"
              ) : signal > redSignalRange ? (
                "red"
              ) : (
                "yellow"
              )
            }`}
          /> */}
          </HeadingStyle>
          <div className="main">
            <TotalNoCard heading={"Status"} total={bStatus} />
            <TotalNoCard heading={"Location"} total={userBLocation} />
            <TotalNoCard heading={"Battery Level"} total={userBattery} />
            <TotalNoCard heading={"Snooze Status"} total={snooze} />
            <TotalNoCard heading={"MAC Number"} total={userBMac} />
            {/* <TotalNoCard heading={"Warranty"} total={warranty} /> */}
          </div>
          <HeadingStyle>
            History&ensp;<i className="bi bi-table heading-icon"></i>
          </HeadingStyle>
          <div className="table-div">
            <table className="table table-hover">
              <thead className="table-head">
                <tr>
                  <th scope="col" className="table-item">
                    #
                  </th>
                  <th scope="col" className="table-item">
                    Device TimeStamp
                  </th>
                  <th scope="col" className="table-item">
                    Notification TimeStamp
                  </th>
                  <th scope="col" className="table-item">
                    Notification
                  </th>
                </tr>
              </thead>
              <tbody>
                {recordStatus === "loading" ? (
                  <tr>
                    <td colSpan={4}>
                      <p className="table-loading">Loading...</p>
                    </td>
                  </tr>
                ) : record.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <p className="table-loading">{tableHistoryStatus}</p>
                    </td>
                  </tr>
                ) : (
                  record.map((item, index) => {
                    const formattedNotification = moment
                      .utc(item.NotificationTimeStamp)
                      .local()
                      .format("YYYY-MM-DD h:mm:ss A");
                    let formattedDeviceTime;
                    if (item.RecordTimeStamp) {
                      formattedDeviceTime = moment
                        .utc(item.RecordTimeStamp)
                        .local()
                        .format("YYYY-MM-DD h:mm:ss A");
                    } else {
                      formattedDeviceTime = moment
                        .utc(item.NotificationTimeStamp)
                        .local()
                        .format("YYYY-MM-DD h:mm:ss A");
                    }

                    return (
                      <tr key={index}>
                        <th scope="row" className="table-item">
                          {index + 1}
                        </th>
                        <td className="table-item2">{formattedDeviceTime}</td>
                        <td className="table-item2">{formattedNotification}</td>
                        <td className="table-item2">
                          {item.NotificationReason}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <DeleteButton
            btnText="DELETE BEACON"
            disabled={isDelete}
            onClick={() => {
              setShowModal(true);
            }}
          />
        </div>
      </PageStyle>
    </BeaconStyle>
  );
};

export default Beacon;
